import { getMembershipUserTypeId, userTypeHasValidMembership } from './membershipFormData';

export const STATUS_BADGE_BASE_CLASS = 'inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium';

export const STATUS_BADGE_CLASSES = {
  amber: 'bg-amber-100 text-amber-700',
  green: 'bg-green-100 text-green-700',
  orange: 'bg-orange-100 text-orange-700',
  red: 'bg-red-100 text-red-700'
};

const getSouthAfricaTime = () => {
  const now = new Date();
  const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
  return new Date(utcTime.getTime() + (2 * 60 * 60 * 1000));
};

export const isMembershipExpired = (expiryDate) => {
  if (!expiryDate) return false;
  const expiry = new Date(expiryDate);
  if (Number.isNaN(expiry.getTime())) return false;
  const now = getSouthAfricaTime();
  return expiry < now;
};

export const getAdminStatusInfo = (memberStatus, { isExpired = false } = {}) => {
  if (memberStatus?.application === 'missingData') {
    return {
      key: 'missingData',
      label: 'Missing Data',
      badgeClass: STATUS_BADGE_CLASSES.red
    };
  }

  if (memberStatus?.application === 'pending') {
    return {
      key: 'requiresApproval',
      label: 'Requires Approval',
      badgeClass: STATUS_BADGE_CLASSES.amber
    };
  }

  if (memberStatus?.application === 'rejected') {
    return {
      key: 'rejected',
      label: 'Rejected',
      badgeClass: STATUS_BADGE_CLASSES.red
    };
  }

  if (memberStatus?.application === 'approved' && memberStatus?.paid !== true) {
    return {
      key: 'pendingPayment',
      label: 'Pending Payment',
      badgeClass: STATUS_BADGE_CLASSES.orange
    };
  }

  if (memberStatus?.application === 'approved' && memberStatus?.paid === true && isExpired) {
    return {
      key: 'expired',
      label: 'Expired',
      badgeClass: STATUS_BADGE_CLASSES.red
    };
  }

  if (isExpired) {
    return {
      key: 'pendingPayment',
      label: 'Pending Payment',
      badgeClass: STATUS_BADGE_CLASSES.orange
    };
  }

  if (memberStatus?.application === 'approved' && memberStatus?.paid === true) {
    return {
      key: 'valid',
      label: 'Valid',
      badgeClass: STATUS_BADGE_CLASSES.green
    };
  }

  return null;
};

export const userTypeSupportsOnlineSignup = (userType) => (
  userType?.membership?.canSignUpOnline === true
);

export const clubHasOnlineSignupUserTypes = (userTypes) => (
  Array.isArray(userTypes) && userTypes.some(userTypeSupportsOnlineSignup)
);

export const getAdminRowStatusDisplay = (memberStatus, userType, { isExpired = false } = {}) => {
  const hasApplication = memberStatus != null;
  const supportsSignup = userTypeSupportsOnlineSignup(userType);

  if (!hasApplication && !supportsSignup) {
    return { kind: 'empty' };
  }

  const statusInfo = hasApplication
    ? getAdminStatusInfo(memberStatus, { isExpired })
    : {
      key: 'missingData',
      label: 'Missing Data',
      badgeClass: STATUS_BADGE_CLASSES.red
    };

  if (!statusInfo) {
    return { kind: 'empty' };
  }

  return { kind: 'badge', clickable: true, ...statusInfo };
};

export const getAdminStatusSortKey = (memberStatus, userType, { isExpired = false } = {}) => {
  const display = getAdminRowStatusDisplay(memberStatus, userType, { isExpired });
  if (display.kind === 'empty') return 99;

  const order = { requiresApproval: 1, rejected: 2, missingData: 3, expired: 4, pendingPayment: 5, valid: 6 };
  return order[display.key] ?? 99;
};

export const ADMIN_STATUS_FILTER_OPTIONS = [
  { key: 'requiresApproval', label: 'Requires Approval' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'missingData', label: 'Missing Data' },
  { key: 'expired', label: 'Expired' },
  { key: 'pendingPayment', label: 'Pending Payment' },
  { key: 'valid', label: 'Valid' },
  { key: 'empty', label: '-' }
];

export const getAdminRowStatusFilterKey = (memberStatus, userType, { isExpired = false } = {}) => {
  const display = getAdminRowStatusDisplay(memberStatus, userType, { isExpired });
  if (display.kind === 'empty') return 'empty';
  return display.key;
};

export const getCardTags = (memberStatus, { isExpired = false } = {}) => {
  const tags = [];

  if (memberStatus?.application === 'missingData') {
    tags.push({
      key: 'missingData',
      label: 'Missing Data',
      badgeClass: STATUS_BADGE_CLASSES.red
    });
    return tags;
  }

  if (memberStatus?.application === 'pending') {
    tags.push({
      key: 'pendingApproval',
      label: 'Pending Approval',
      badgeClass: STATUS_BADGE_CLASSES.amber
    });
    return tags;
  }

  if (memberStatus?.application === 'rejected') {
    tags.push({
      key: 'rejected',
      label: 'Rejected',
      badgeClass: STATUS_BADGE_CLASSES.red
    });
    return tags;
  }

  if (memberStatus?.application === 'approved' && memberStatus?.paid !== true) {
    tags.push({
      key: 'approved',
      label: 'Approved',
      badgeClass: STATUS_BADGE_CLASSES.green
    });
    tags.push({
      key: 'requiresPayment',
      label: 'Requires Payment',
      badgeClass: STATUS_BADGE_CLASSES.orange
    });
    return tags;
  }

  if (memberStatus?.application === 'approved' && memberStatus?.paid === true && isExpired) {
    tags.push({
      key: 'expired',
      label: 'Expired',
      badgeClass: STATUS_BADGE_CLASSES.red
    });
    return tags;
  }

  if (isExpired) {
    tags.push({
      key: 'requiresPayment',
      label: 'Requires Payment',
      badgeClass: STATUS_BADGE_CLASSES.orange
    });
  }

  return tags;
};

export const hasCompletedApplication = (memberStatus) => (
  memberStatus?.application === 'approved' && memberStatus?.paid === true
);

export const hasActiveMembership = (user) => (
  Boolean(
    user?.userTypeId
    && String(user.userTypeId) !== '0'
    && userTypeHasValidMembership({ membership: user?.membership })
    && !isMembershipExpired(user?.expiryDate)
  )
);

export const isExpiredApprovedMember = (memberStatus, expiryDate) => (
  hasCompletedApplication(memberStatus) && isMembershipExpired(expiryDate)
);

export const resolveMemberStatusAfterApplicationSave = (memberStatus, { valid = true } = {}) => {
  if (!memberStatus) return null;

  if (!valid) {
    const canMarkMissingData = memberStatus.application === 'approved'
      || memberStatus.application === 'missingData';

    if (canMarkMissingData) {
      return { paid: memberStatus.paid, application: 'missingData' };
    }

    return memberStatus;
  }

  if (memberStatus.application === 'missingData') {
    return { paid: memberStatus.paid, application: 'approved' };
  }

  return memberStatus;
};

const escapeHtml = (text) => String(text)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

export const buildRejectionBannerHtml = (reason, { audience = 'member' } = {}) => {
  const escapedReason = escapeHtml(reason);
  const title = audience === 'admin' ? 'Application rejected' : 'Your application was not approved';

  return `
    <div style="margin:0 0 20px;padding:12px 16px;border:1px solid #fecaca;border-radius:8px;background:#fef2f2;color:#991b1b;font-family:system-ui,sans-serif;font-size:14px;line-height:1.5;">
      <p style="margin:0;font-weight:600;">${title}</p>
      <p style="margin:8px 0 0;white-space:pre-wrap;">${escapedReason}</p>
    </div>
  `;
};

export const buildApprovalBannerHtml = ({ requiresPayment = false, requiresContactClub = false } = {}) => {
  let instructionLine = '';
  if (requiresPayment) {
    instructionLine = '<p style="margin:8px 0 0;">Please pay the fee to complete your application.</p>';
  } else if (requiresContactClub) {
    instructionLine = '<p style="margin:8px 0 0;"><a href="/club-contacts" target="_top" style="color:#166534;text-decoration:underline;font-weight:600;">Contact the club</a> to arrange payment.</p>';
  }

  return `
    <div style="margin:0 0 20px;padding:12px 16px;border:1px solid #bbf7d0;border-radius:8px;background:#f0fdf4;color:#166534;font-family:system-ui,sans-serif;font-size:14px;line-height:1.5;">
      <p style="margin:0;font-weight:600;">Your application has been approved</p>
      ${instructionLine}
    </div>
  `;
};

export const buildMembershipSwitchWarningHtml = (currentMembershipName, { requiresApproval = false } = {}) => {
  const escapedName = escapeHtml(currentMembershipName);
  const approvalNote = requiresApproval
    ? '<p style="margin:8px 0 0;">If club approval is required, your current membership will only change once your application is approved and complete.</p>'
    : '';

  return `
    <div style="margin:0 0 20px;padding:12px 16px;border:1px solid #fecaca;border-radius:8px;background:#fef2f2;color:#991b1b;font-family:system-ui,sans-serif;font-size:14px;line-height:1.5;">
      <p style="margin:0;font-weight:600;">WARNING</p>
      <p style="margin:8px 0 0;">Submitting an application for this membership will replace your current <strong>${escapedName}</strong> membership. Your remaining membership period may be forfeited.</p>
      ${approvalNote}
    </div>
  `;
};

export const shouldRefreshMembershipSession = (profileResponse, jwtUser) => {
  if (!profileResponse || !jwtUser) return false;

  const normalizeExpiryMs = (value) => {
    if (!value) return null;
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
  };

  const profileExpiryMs = normalizeExpiryMs(profileResponse.expiresOn);
  const jwtExpiryMs = normalizeExpiryMs(jwtUser.expiryDate);

  if (profileExpiryMs !== jwtExpiryMs) {
    return true;
  }

  const memberStatus = profileResponse.memberStatus;
  const applicationUserTypeId = getMembershipUserTypeId(profileResponse.metaData);

  if (!applicationUserTypeId || !memberStatus) return false;
  if (memberStatus.application !== 'approved' || memberStatus.paid !== true) return false;

  return String(jwtUser.userTypeId) !== String(applicationUserTypeId);
};
